import { useContext, useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Divider,
    Chip,
    Grid,
    Box,
    Skeleton,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DiamondIcon from '@mui/icons-material/Diamond';

import { capitalizeFirstChar } from '../../helper/Helper';
import { UserContext } from '../../context/UserContext';
import PageContainer from '../../components/page-container/PageContainer';
import { FORM_CONTAINER_SX } from '../../theme/formStyles';

const ProfileSkeleton = () => (
    <Paper elevation={3} sx={FORM_CONTAINER_SX}>
        {/* Greeting skeleton */}
        <Skeleton variant="text" width={300} height={48} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />

        {/* Profile section skeleton */}
        <Skeleton variant="text" width={100} height={32} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Skeleton variant="text" width={50} height={24} />
                    <Skeleton variant="rounded" width={80} height={32} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="text" width={60} height={24} />
                    <Skeleton variant="rounded" width={70} height={32} />
                </Box>
            </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Company section skeleton */}
        <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Skeleton variant="text" width="50%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="40%" height={24} />
            </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Subscription section skeleton */}
        <Skeleton variant="text" width={140} height={32} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Skeleton variant="text" width={40} height={24} />
                    <Skeleton variant="circular" width={24} height={24} />
                </Box>
                <Skeleton variant="text" width="45%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="text" width={60} height={24} />
                    <Skeleton variant="rounded" width={70} height={32} />
                </Box>
            </Grid>
        </Grid>
    </Paper>
);

export default function ProfilePage() {
    const { user } = useContext(UserContext);
    const [userData, setUserData] = useState(null);
    const [companyData, setCompanyData] = useState(null);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        if (!user?.uid) return;
        setUserData(user.user || null);
        setCompanyData(user.company || null);
        setSubscriptionData(user.subscription || null);
        setLoading(false)
    }, [user]);

    if (loading) {
        return (
            <PageContainer>
                <ProfileSkeleton />
            </PageContainer>
        );
    }

    if (!userData) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 8 }}>
                <Typography variant="h6">User profile not found.</Typography>
            </Container>
        );
    }

    return (
        <PageContainer>
            <Paper elevation={3} sx={FORM_CONTAINER_SX}>
                <Typography variant="h3" gutterBottom>Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}{userData?.name ? `, ${userData?.name}` : '.'}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Profile</Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Typography><strong>Name:</strong> {userData.name}</Typography>
                        <Typography><strong>Email:</strong> {userData.email}</Typography>
                        <Typography><strong>Role:</strong></Typography> <Chip label={capitalizeFirstChar(userData.role)} />
                        <Typography><strong>Status:</strong></Typography> <Chip label={userData.isActive ? 'Active' : 'Inactive'} color={userData.isActive ? 'success' : 'warning'} />
                    </Grid>

                </Grid>

                {companyData && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>Company</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Typography><strong>Name:</strong> {companyData.name}</Typography>
                                <Typography><strong>Owner:</strong> {companyData.ownerId === user?.uid ? 'Yes' : 'No'}</Typography>
                            </Grid>
                        </Grid>
                    </>
                )}

                {subscriptionData && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>Subscription</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Typography><strong>Plan:</strong> {capitalizeFirstChar(subscriptionData.planType)}</Typography>
                                {subscriptionData?.planType === 'basic' &&
                                    <StarIcon sx={{ color: '#CD7F32' }} />
                                }
                                {subscriptionData.planType === 'pro' &&
                                    <WorkspacePremiumIcon sx={{ color: '#C0C0C0', fontSize: 24 }} />
                                }
                                {subscriptionData?.planType === 'enterprise' &&
                                    <DiamondIcon sx={{ color: '#FFD700' }} />
                                }
                                <Typography><strong>Start Date:</strong> {subscriptionData.startDate?.toDate().toLocaleDateString()}</Typography>
                                <Typography><strong>End Date:</strong> {subscriptionData.endDate?.toDate().toLocaleDateString()}</Typography>
                                <Typography><strong>Status:</strong></Typography> <Chip label={subscriptionData.isActive ? 'Active' : 'Expired'} color={subscriptionData.isActive ? 'success' : 'error'} />
                            </Grid>
                        </Grid>
                    </>
                )}

            </Paper>
        </PageContainer>
    );
}
